import React, { useCallback, useEffect, useState } from 'react';
import { aiSettingsApi, AiSettings as Settings, CatalogueModel } from '../../api/ai-settings';
import { useAuth } from '../../hooks/useAuth';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Spinner } from '../../components/ui/Spinner';
import { RoutingTab } from './RoutingTab';
import { ActionsTab } from './ActionsTab';
import { ModelsTab } from './ModelsTab';

type Tab = 'routing' | 'actions' | 'models';
const TABS: { key: Tab; label: string }[] = [
  { key: 'routing', label: 'Routing' },
  { key: 'actions', label: 'Actions' },
  { key: 'models', label: 'Models & cost' },
];

export const AiSettingsPage: React.FC = () => {
  usePage('AI Settings');
  const { can } = useAuth();
  const toast = useToast();
  const canManage = can('ai_settings.manage');
  const [tab, setTab] = useState<Tab>('routing');
  const [settings, setSettings] = useState<Settings | null>(null);
  const [catalogue, setCatalogue] = useState<CatalogueModel[]>([]);

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([aiSettingsApi.settings(), aiSettingsApi.models()]);
      setSettings(s);
      setCatalogue(c);
    } catch (err) {
      toast.error(err, 'Could not load AI settings.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title="AI Settings"
        description="Which provider answers, who covers when it is down, and the model each action uses."
      />
      <div role="tablist" style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--neutral-300)' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 16px',
              background: 'none',
              border: 'none',
              borderBottom: `2px solid ${tab === t.key ? 'var(--blue-600)' : 'transparent'}`,
              fontWeight: tab === t.key ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      {!settings ? (
        <Spinner />
      ) : tab === 'routing' ? (
        <RoutingTab routing={settings.routing} providers={settings.providers} canManage={canManage} onChanged={load} />
      ) : tab === 'actions' ? (
        <ActionsTab settings={settings} catalogue={catalogue} canManage={canManage} onChanged={load} />
      ) : (
        <ModelsTab settings={settings} catalogue={catalogue} canManage={canManage} onChanged={load} />
      )}
    </div>
  );
};
